import { useState, useCallback, useMemo } from 'react';

/**
 * @typedef {Object} FilterDefinition
 * @property {string} key - Stable filter id.
 * @property {string} [label] - Human-readable label shown in the select.
 * @property {(row: any) => any} getValue - Value used for equality matching.
 * @property {string[]} [options] - Optional explicit option list.
 */

/**
 * @typedef {Object} ColumnDescriptor
 * @property {'number' | 'text'} type - How the column is compared when sorting.
 * @property {(row: any) => any} accessor - Extracts the sort value from a row.
 */

/**
 * @typedef {Object} SortState
 * @property {string|null} key - Active sort column key, or null when unsorted.
 * @property {'asc'|'desc'|null} dir - Sort direction, or null when unsorted.
 */

/**
 * Determines whether a numeric sort value should be treated as "missing".
 * Missing values (null/undefined/NaN) always sort after present values.
 *
 * @param {any} value
 * @returns {boolean}
 */
function isMissingNumber(value) {
  return value == null
    || (typeof value === 'string' && value.trim() === '')
    || Number.isNaN(Number(value));
}

/**
 * Normalizes a text sort value to a lowercased, trimmed string.
 *
 * @param {any} value
 * @returns {string}
 */
function normalizeText(value) {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Builds a comparator for the given column descriptor that:
 *  - compares numerically (subtraction) or as case-insensitive text,
 *  - pushes missing values to the end regardless of direction,
 *  - falls back to the original index for stability (decorate-sort-undecorate).
 *
 * @param {ColumnDescriptor} column
 * @param {'asc'|'desc'} dir
 * @returns {(a: { row: any, idx: number }, b: { row: any, idx: number }) => number}
 */
function makeComparator(column, dir) {
  const directionFactor = dir === 'desc' ? -1 : 1;

  if (column.type === 'number') {
    return (a, b) => {
      const av = column.accessor(a.row);
      const bv = column.accessor(b.row);
      const aMissing = isMissingNumber(av);
      const bMissing = isMissingNumber(bv);

      // Missing values always trail, independent of direction.
      if (aMissing && bMissing) return a.idx - b.idx;
      if (aMissing) return 1;
      if (bMissing) return -1;

      const diff = Number(av) - Number(bv);
      if (diff !== 0) return directionFactor * diff;
      return a.idx - b.idx;
    };
  }

  // Text comparator.
  return (a, b) => {
    const av = normalizeText(column.accessor(a.row));
    const bv = normalizeText(column.accessor(b.row));
    const aMissing = av === '';
    const bMissing = bv === '';

    // Empty/missing strings always trail, independent of direction.
    if (aMissing && bMissing) return a.idx - b.idx;
    if (aMissing) return 1;
    if (bMissing) return -1;

    const cmp = av.localeCompare(bv);
    if (cmp !== 0) return directionFactor * cmp;
    return a.idx - b.idx;
  };
}

/**
 * Shared search / filter / sort logic for the employee tables.
 *
 * The hook derives a view (`viewRows`) over the source `rows` without mutating
 * them. Search is a case-insensitive substring match over `getSearchText`,
 * filters are ANDed via each filter's `getValue`, and sort orders the result by
 * a column descriptor using a stable, missing-last comparator.
 *
 * @param {Object} config
 * @param {Array} config.rows - Source rows (unfiltered, unsorted).
 * @param {(row: any) => string} [config.getSearchText] - Concatenated searchable text for a row.
 * @param {FilterDefinition[]} [config.filters] - Filter definitions.
 * @param {Object.<string, ColumnDescriptor>} [config.columns] - Map of column key -> sort descriptor.
 * @param {SortState} [config.initialSort] - Sort applied when the table first opens.
 * @returns {{
 *   viewRows: Array,
 *   search: string,
 *   setSearch: (v: string) => void,
 *   activeFilters: Object.<string, string>,
 *   setFilter: (key: string, value: string) => void,
 *   clearFilters: () => void,
 *   clearAll: () => void,
 *   sort: SortState,
 *   toggleSort: (columnKey: string) => void,
 *   totalCount: number,
 *   activeCount: number,
 *   isActive: boolean
 * }}
 */
export function useEmployeeTableView({
  rows,
  getSearchText,
  filters = [],
  columns = {},
  initialSort = { key: null, dir: null },
} = {}) {
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [sort, setSort] = useState(initialSort);

  /**
   * Sets a single filter value. An empty string clears that filter.
   */
  const setFilter = useCallback((key, value) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (value === '' || value == null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  /**
   * Clears all active filters (leaves search and sort untouched).
   */
  const clearFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  /**
   * Clears search and all filters (leaves sort untouched).
   */
  const clearAll = useCallback(() => {
    setSearch('');
    setActiveFilters({});
  }, []);

  /**
   * Cycles the sort for a column: not-active -> asc -> desc -> cleared.
   * Activating a different column resets it to ascending.
   */
  const toggleSort = useCallback((columnKey) => {
    setSort((prev) => {
      if (prev.key !== columnKey) {
        return { key: columnKey, dir: 'asc' };
      }
      if (prev.dir === 'asc') {
        return { key: columnKey, dir: 'desc' };
      }
      if (prev.dir === 'desc') {
        return { key: null, dir: null };
      }
      return { key: columnKey, dir: 'asc' };
    });
  }, []);

  const sourceRows = Array.isArray(rows) ? rows : [];

  // Derive the view; recompute only when an input changes.
  const viewRows = useMemo(() => {
    let result = sourceRows;

    // 1. Search (case-insensitive substring; whitespace-only treated as empty).
    const query = search.trim().toLowerCase();
    if (query !== '' && typeof getSearchText === 'function') {
      result = result.filter((row) =>
        String(getSearchText(row) ?? '').toLowerCase().includes(query)
      );
    }

    // 2. Filter (AND across active filters via each filter's getValue).
    const activeFilterDefs = filters.filter(
      (def) => activeFilters[def.key] != null && activeFilters[def.key] !== ''
    );
    if (activeFilterDefs.length > 0) {
      result = result.filter((row) =>
        activeFilterDefs.every(
          (def) => def.getValue(row) === activeFilters[def.key]
        )
      );
    }

    // 3. Sort (stable, missing-last) when a column + direction are set.
    if (sort.key && sort.dir && columns[sort.key]) {
      const comparator = makeComparator(columns[sort.key], sort.dir);
      result = result
        .map((row, idx) => ({ row, idx }))
        .sort(comparator)
        .map((entry) => entry.row);
    } else if (result === sourceRows) {
      // Always return a new array so the source is never aliased/mutated.
      result = result.slice();
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRows, search, activeFilters, sort]);

  const totalCount = sourceRows.length;
  const activeCount = viewRows.length;
  // Sorting changes row order but does not narrow the view. Keep the toolbar's
  // Clear/count state specific to controls that actually filter rows.
  const isActive =
    search.trim() !== '' ||
    Object.keys(activeFilters).length > 0;

  return {
    viewRows,
    search,
    setSearch,
    activeFilters,
    setFilter,
    clearFilters,
    clearAll,
    sort,
    toggleSort,
    totalCount,
    activeCount,
    isActive,
  };
}
